import React, { useContext, useState, useEffect } from 'react';
import {
   Button, ButtonGroup, Card, CardBody, CardTitle, Col, Input,
   Modal, ModalBody, ModalFooter, ModalHeader, Row, Table
} from 'reactstrap';
import { useNavigate } from 'react-router-dom';
import BarcodeScannerComponent from "react-qr-barcode-scanner";
import { toast } from "react-toastify";
import { ServiceThreadSpinner } from '.';
import { DesktopContainer } from '../layouts';
import { api, constants, UserContext, storage } from '../utils';

export default function ChangeLabelWeight() {
   const navigate = useNavigate();
   const { currentUser } = useContext(UserContext);
   const [barcode, setBarcode] = useState(null);
   const [isCase, setIsCase] = useState(true);
   const [loaded, setLoaded] = useState(false);
   const [manualEntry, setManual] = useState(null);
   const [model, setModel] = useState({});
   const [open, setOpen] = useState(null);
   const [review, setReview] = useState(false);
   const [spin, setSpinner] = useState(false);
   const sleep = ms => new Promise(r => setTimeout(r, ms));
   let modalClassName = currentUser.theme ? `${currentUser.theme}` : "lightTheme";
   let savedDevice = JSON.parse(storage.getItem("deviceType"));
   const isMobile = savedDevice.isMobile;
   const hasScanner = savedDevice.hasScanner;
   const canEdit = currentUser.isAdmin || currentUser.isPowerUser || currentUser.isWarehouseEdit;

   useEffect(() => {
      if (!canEdit) {
         toast.error("You do not have permission to edit label weights");
         cancelPage();
      }
      inputFocus("searchInput");
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [])

   useEffect(() => {
      // Is current barcode a production WO case label or an IV transfer label?
      if (barcode) {
         // QR codes will start with B for Bulk IP case labels and with C for FG and non-bulk IP labels
         let tmp = barcode.toUpperCase().substring(0, 1);
         setIsCase(tmp === "B" || tmp === "C" || !isNaN(tmp));
      }

      // if device has built-in scanner see if a scan was completed
      if (hasScanner && barcode && !manualEntry) {
         setLoaded(false);
         getLabel();
      }
      if (manualEntry)
         inputFocus("manualInput");
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [barcode])

   useEffect(() => {
      async function load() {
         await sleep(50);  // allows time for input to be recognized for focus
         if (!loaded) {
            inputFocus("searchInput");
         }
         else {
            inputFocus("netWt");
         }
      }
      load();
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [loaded])

   useEffect(() => {
      if (manualEntry) {
         inputFocus("manualInput");
         return;
      }

      if (!barcode) {
         inputFocus("searchInput");
         return;
      }

      setLoaded(false);
      getLabel();
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [manualEntry])

   function cancelPage() {
      var nav = constants.ENDPOINTS.addInventory;
      navigate(nav);
   }

   async function cancelReview() {
      await sleep(50); // allows time for input to be recognized for focus
      setReview(false);
      inputFocus("netWt");
   }

   async function doReview() {
      await sleep(50);  // allows time for input to be recognized for focus

      if (!model.netWeight || model.netWeight <= 0) {
         toast.error("Net weight must be > 0");
         inputFocus("netWt");
         return;
      }
      else if (model.netWeight > 1300) { //else if (model.netWeight > oldWgt)
         toast.error('Net weight cannot exceed 1300 lbs');
         inputFocus("netWt");
         return;
      }

      setReview(true);
   }

   function getLabel() {
      if (!loaded && barcode) {
         // If a QR code was scanned then parse out just the barcode from the data
         var code = constants.GetRealBarcode(barcode);
         if (code.length < 2) return;

         setIsCase(code.substring(0, 2) !== "IV");
         setSpinner(true);
         api
            .post(`Mobile/LookupWeightChangeItem/${code}`)
            .then(response => {
               console.log(response);
               if (response.data.success) {
                  setModel(response.data.item);
                  setLoaded(true);
               }
               else {
                  toast.error(response.data.message);
                  setLoaded(false);
               }
            })
            .catch((error) => {
               api.catchHandler(error);
               toast.error(error);
               setLoaded(false);
            })
            .finally(() => {
               setBarcode(null);
               setSpinner(false);
            });
      }
   }

   const handleFocus = (event) => event.target.select();

   const handleKeypress = e => {
      // On key press for search input field
      if (e.charCode === 13 || e.key === 'Enter') {
         setManual(null);
         return;
      }
      inputFocus("manualInput");
   };

   async function inputFocus(fieldname) {
      await sleep(80);  // allows time for input to be recognized
      const el = document.querySelector(`input[name=${fieldname}]`);
      if (el !== null) {
         await el.focus();
      }
   }

   async function lookup(barcode) {
      // On change of search input field
      setBarcode(barcode);

      if (manualEntry) {
         inputFocus("manualInput");
         return;
      }
      inputFocus("searchInput");
   }

   function onChange(field, value) {
      let changes = Object.assign({}, model);
      changes[field] = value;
      setModel(changes);
   }

   function reset() {
      setModel({});        // clear any previous label model
      setBarcode(null);    // clear any barcode previously scanned
      setLoaded(false);    // starting over, must scan or enter a barcode
      setOpen(null);       // hide barcode scanner dialog
      setReview(false);    // hide review segment
      setSpinner(false);   // hide spinner
   }

   function updateLabel() {
      setSpinner(true);
      api
         .post(`Mobile/WeightChangeUpdate`, model)
         .then(response => {
            if (response.data.success) {
               toast.success('Label was successfully updated');
               cancelPage();
            }
            else {
               toast.error(response.data.message);
            }
         })
         .catch((error) => {
            api.catchHandler(error);
            toast.error(error);
         })
         .finally(() => {
            reset();
         });
   }

   return (
      <>
         <DesktopContainer screenName="Label Weight Change">
            <ServiceThreadSpinner on={spin}>
               {!loaded ?
                  <div>
                     <Row><Col className="h6 fw-bold" xs="12">Scan an IP or PM item label to change the label weight</Col></Row>
                     <Row><Col><hr className="mb-0 my-1" /></Col></Row>
                     <br />
                     {/* Scan Button */}
                     {!hasScanner && isMobile ?
                        <div>
                           <Row className="justify-content-center">
                              <Col xs="12">
                                 <Button title="scan entry" className="shredButton w-100 btn-md" onClick={() => setOpen(1)}>
                                    Scan Barcode
                                 </Button>
                              </Col>
                           </Row>
                           <Row className="lineheight8">&nbsp;</Row>
                        </div>
                        : null
                     }

                     <div>
                        {/* Manual Entry Button */}
                        <Row className="justify-content-center">
                           <Col xs="12">
                              <Button title="manual entry" className="shredButton w-100 btn-md" onClick={() => setManual(1)}>
                                 Manual Entry
                              </Button>
                           </Col>
                        </Row>
                        {/* Built-in Scanner Barcode */}
                        {isMobile ?
                           <div>
                              <Row className="lineheight8">&nbsp;</Row>
                              <Row className="justify-content-center">
                                 <Col xs="12">
                                    <Input
                                       name="searchInput"
                                       placeholder="Focus here to scan barcode"
                                       className="h-100"
                                       onChange={(e) => lookup(e.target.value)}
                                    />
                                 </Col>
                              </Row>
                           </div>
                           : null
                        }
                     </div>
                  </div>
                  : null
               }
               <Row><Col><hr className="my-0" /></Col></Row>
               <br />

               {loaded && !review ?
                  <div>
                     <Card className="mobileBlueBgAlt text-white">
                        <CardBody className="mobileBlueBgAlt text-white">
                           <CardTitle style={{ textAlign: "center" }} className="mobileBlueBg text-white my-0 py-0 px-0">Enter Weight</CardTitle>
                           <Row><Col><hr className="my-0 text-white" /></Col></Row>
                           <br />
                           <Table borderless size="sm">
                              <tbody>
                                 <tr>
                                    <td className="data-cell my-0 py-0" width="85px">
                                       <label className="text-black h6 fw-bold my-0">Item:</label>
                                    </td>
                                    <td className="data-cell my-0 py-0">
                                       <label className="text-black h6 mx-1 my-0">{model.itemWithDesc}</label>
                                    </td>
                                 </tr>
                                 {isCase ?
                                    <tr>
                                       <td className="data-cell my-0 py-0">
                                          <label className="text-black h6 fw-bold my-0">Case:</label>
                                       </td>
                                       <td className="data-cell my-0 py-0">
                                          <label className="text-black h6 mx-1 my-0">{model.caseDisplayId}</label>
                                       </td>
                                    </tr>
                                    : null
                                 }
                                 <tr>
                                    <td className="data-cell my-0 py-0">
                                       <label className="text-black h6 fw-bold my-0">Net Wt:</label>
                                    </td>
                                    <td className="data-cell my-0 py-0">
                                       <Input
                                          name="netWt"
                                          type="number"
                                          className="h-100 py-1"
                                          value={model.netWeight}
                                          max={9999}
                                          min={1}
                                          required
                                          onFocus={handleFocus}
                                          onChange={(e) => { onChange("netWeight", e.target.value) }}
                                       />
                                    </td>
                                 </tr>
                              </tbody>
                           </Table>
                        </CardBody>
                     </Card>
                     <br />
                     <div>
                        <Row><Col><hr className="my-0" /></Col></Row>
                        {model.netWeight > 0 ?
                           <Row>
                              <Col xs="12">
                                 <Button title="Save" className="shredButton w-100 btn-lg my-3" onClick={() => doReview()}>
                                    OK
                                 </Button>
                              </Col>
                           </Row>
                           : null
                        }
                        <Row>
                           <Col xs="12">
                              <Button title="Cancel" className="btn w-100 btn-lg my-3" onClick={() => reset()}>
                                 Cancel
                              </Button>
                           </Col>
                        </Row>
                     </div>
                  </div>
                  : null
               }


               {review && model ?
                  <div>
                     <Card className="mobileBlueBgAlt py-0">
                        <CardBody className="mobileBlueBgAlt">
                           <CardTitle style={{ textAlign: "center" }} className="mobileBlueBg text-white my-0 py-0 px-0">Weight Review</CardTitle>
                           <Row className="text-white my-1" style={{ textAlign: "center" }}><Col>Please review the weight information</Col></Row>
                           <Row><Col><hr className="text-white my-1" /></Col></Row>
                           <br />
                           <Table borderless size="sm">
                              <tbody>
                                 <tr>
                                    <td className="data-cell my-0 py-0" width="65px">
                                       <label className="text-black h6 fw-bold my-0">Item:</label>
                                    </td>
                                    <td className="data-cell my-0 py-0">
                                       <label className="text-black h6 mx-1 my-0">{model.itemWithDesc}</label>
                                    </td>
                                 </tr>
                                 {isCase ?
                                    <tr>
                                       <td className="data-cell my-0 py-0">
                                          <label className="text-black h6 fw-bold my-0">Case:</label>
                                       </td>
                                       <td className="data-cell my-0 py-0">
                                          <label className="text-black h6 mx-1 my-0">{model.caseDisplayId}</label>
                                       </td>
                                    </tr>
                                    : null
                                 }
                                 <tr>
                                    <td className="data-cell my-0 py-0">
                                       <label className="text-black h6 fw-bold my-0">Net:</label>
                                    </td>
                                    <td className="data-cell my-0 py-0">
                                       <label className="text-black h6 mx-1 my-0">{model.netWeight}</label>
                                    </td>
                                 </tr>
                              </tbody>
                           </Table>
                           <Row><Col><hr className="text-white my-1" /></Col></Row>
                           <Row>
                              <Col xs="12">
                                 <Button className="shredButton w-100 btn-md my-1" onClick={() => updateLabel()}>
                                    Update Label
                                 </Button>
                              </Col>
                           </Row>
                           <Row>
                              <Col xs="12">
                                 <Button className="btn w-100 btn-md my-2" onClick={() => cancelReview()}>
                                    Edit Label
                                 </Button>
                              </Col>
                           </Row>
                        </CardBody>
                     </Card>
                  </div>
                  : null
               }


               {/* Scan a Barcode */}
               <Modal isOpen={open !== null} contentClassName={modalClassName}>
                  <ModalHeader children="Scan a Barcode" />
                  <ModalBody
                     children={
                        <BarcodeScannerComponent
                           height={350}
                           width={350}
                           onUpdate={(err, result) => {
                              if (result) {
                                 setBarcode(result.text.toUpperCase());
                                 setOpen(null);
                                 setLoaded(false);
                                 getLabel();
                              }
                              else {
                                 setBarcode(null);
                              }
                           }}
                           facingMode={"environment"}
                        />
                     }
                  />
                  <ModalFooter>
                     <Button
                        className="shredButton ms-7"
                        onClick={() => {
                           setOpen(null);
                           inputFocus("searchInput");
                        }}>Cancel
                     </Button>
                  </ModalFooter>
               </Modal>


               {/* Manual Entry of a Barcode */}
               <Modal isOpen={manualEntry !== null} contentClassName={modalClassName}>
                  <ModalHeader children="Enter Barcode" />
                  <ModalBody
                     children={
                        <Input
                           name="manualInput"
                           placeholder="Enter barcode"
                           className="h-100"
                           onChange={(e) => lookup(e.target.value.toUpperCase())}
                           onKeyPress={handleKeypress}
                        />
                     }
                  />
                  <ModalFooter>
                     <ButtonGroup>
                        <Button children="Retrieve" className="button-success" onClick={() => { setManual(null); }} />
                        <Button children="Cancel" className="button-danger" onClick={() => {
                           setBarcode(null);
                           setManual(null);
                           inputFocus("searchInput");
                        }} />
                     </ButtonGroup>
                  </ModalFooter>
               </Modal>

            </ServiceThreadSpinner>
         </DesktopContainer >
      </>
   );
}
